import React, { useEffect, useState, useCallback } from 'react';
import {
    TextField, Button, MenuItem, Table, TableHead,
    TableRow, TableCell, TableBody, Container, Grid,
    Typography, CircularProgress, Alert, Snackbar,
    Box, TableContainer, Tooltip, IconButton,
    Card, CardHeader, CardContent, Avatar,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Inventory as InventoryIcon,
    Category as CategoryIcon,
    Straighten as MeasureIcon,
    MoreVert as MoreIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    DocumentScannerOutlined,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GroupWorkIcon from '@mui/icons-material/GroupWork'; // for bench recruiters
import PersonSearchIcon from '@mui/icons-material/PersonSearch'; // for bench candidates
import BusinessIcon from '@mui/icons-material/Business'; // for JnP
import StorefrontIcon from '@mui/icons-material/Storefront'; // for Bench Sales
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'; // for Both




const CATEGORY_OPTIONS = [
    { 
        value: 'jnp', 
        label: 'JnP',
        icon: <BusinessIcon sx={{ color: '#1976d2' }} />
    },
    { 
        value: 'bench_sales', 
        label: 'Bench Sales',
        icon: <StorefrontIcon sx={{ color: '#388e3c' }} />
    },
    // { 
    //     value: 'both', 
    //     label: 'Both',
    //     icon: <CompareArrowsIcon sx={{ color: '#f57c00' }} />
    // }
];

const DEFAULT_FORM_STATE = {
    item_name: '',
    item_description: '',
    category: ''
};

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)'
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: alpha(theme.palette.primary.light, 0.05),
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.light, 0.15),
    }
}));

export default function ItemsManagement() {
    const theme = useTheme();

    // State Management
    const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
    const [formErrors, setFormErrors] = useState({
        item_name: false,
        item_description: false,
        category: false
    });
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Validate form fields
    const validateForm = () => {
        const errors = {
            item_name: !formData.item_name || formData.item_name.trim() === '',
            item_description: !formData.item_description || formData.item_description.trim() === '',
            category: !formData.category || formData.category.trim() === ''
        };
        setFormErrors(errors);
        return !Object.values(errors).some(error => error);
    };

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: false }));
    };

    const resetForm = () => {
        setFormData(DEFAULT_FORM_STATE);
        setFormErrors({
            item_name: false,
            item_description: false,
            category: false,
            unit_of_measure: false
        });
        setEditingId(null);
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Filter handling
    const handleFilterChange = (event) => {
        setCategoryFilter(event.target.value);
    };

    // Apply filters
    useEffect(() => {
        if (categoryFilter === 'all') {
            setFilteredItems(items);
        } else {
            setFilteredItems(items.filter(item => item.category === categoryFilter));
        }
    }, [items, categoryFilter]);

    // Data Fetching
    const fetchItems = useCallback(async () => {
        try {
            setFetching(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/jnp_service_items`);
            setItems(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch items. Please try again later.');
            console.error('Error fetching items:', err);
        } finally {
            setFetching(false);
        }
    }, []);

    // CRUD Operations
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showNotification('Please fill in all required fields.', 'error');
            return;
        }

        try {
            setLoading(true);
            if (editingId) {
                await axios.put(`${process.env.REACT_APP_API_URL}/jnp_service_items/${editingId}`, formData);
                showNotification('Item updated successfully');
            } else {
                console.log('Submitting form data:', formData);
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/jnp_service_items`, formData);
                console.log('Server response:', response.data);
                showNotification('Item added successfully');
            }
            resetForm();
            fetchItems();
        } catch (err) {
            showNotification(`Failed to ${editingId ? 'update' : 'add'} item. Please try again.`, 'error');
            console.error('Error submitting form:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            setLoading(true);
            await axios.delete(`${process.env.REACT_APP_API_URL}/jnp_service_items/${id}`);
            showNotification('Item deleted successfully');
            fetchItems();
        } catch (err) {
            showNotification('Failed to delete item. Please try again.', 'error');
            console.error('Error deleting item:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setFormData({
            item_name: item.item_name,
            item_description: item.item_description,
            unit_of_measure: item.unit_of_measure,
            category: item.category
        });
        setEditingId(item.id);
    };

    // Effects
    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
        

            {/* Form Card */}
            <StyledCard sx={{ mb: 4 }}>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {editingId ? <EditIcon /> : <AddIcon />}
                        </Avatar>
                    }
                    title={
                        <Typography variant="h6" component="div">
                            {editingId ? 'Edit Service Item' : 'Create New Service Item'}
                        </Typography>
                    }
                    subheader={editingId ? 'Update existing item details' : 'Add a new item to your inventory'}
                    action={
                        <Tooltip title="Refresh Items data">
                            <IconButton
                                onClick={fetchItems}
                                color="primary"
                                disabled={fetching}
                                size="large"
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.primary.light, 0.05)
                    }}
                />
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3} alignItems="flex-end">
                            {/* Item Type Dropdown */}
                            <Grid item xs={12} md={4} sx={{ width: "20%" }}>
                                <TextField
                                    label="Item Type *"
                                    name="item_name"
                                    value={formData.item_name || ''}
                                    onChange={handleChange}
                                    fullWidth
                                    error={formErrors.item_name}
                                    helperText={formErrors.item_name ? "Item type is required" : "Enter the type of item"}
                                    size="medium"
                                    variant="outlined"
                                    InputProps={{
                                        sx: {
                                            height: '56px'
                                        },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CategoryIcon color="action" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            {/* Category Dropdown */}
                            <Grid item xs={12} md={4} sx={{ width: "20%" }}>
                                <TextField
                                    select
                                    label="Category *"
                                    name="category"
                                    value={formData.category || ''}
                                    onChange={handleChange}
                                    fullWidth
                                    error={formErrors.category}
                                    helperText={formErrors.category ? "Category is required" : "Select the item category"}
                                    size="medium"
                                    variant="outlined"
                                    InputProps={{
                                        sx: {
                                            height: '56px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }
                                    }}
                                    SelectProps={{
                                        renderValue: (selected) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {CATEGORY_OPTIONS.find(opt => opt.value === selected)?.icon}
                                                <Typography variant="body1">
                                                    {CATEGORY_OPTIONS.find(opt => opt.value === selected)?.label || 'Select a category'}
                                                </Typography>
                                            </Box>
                                        )
                                    }}
                                >
                                    {CATEGORY_OPTIONS.map(option => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            sx={{
                                                minHeight: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2
                                            }}
                                        >
                                            {option.icon}
                                            <Typography variant="body1">
                                                {option.label}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>



                            {/* Description Field */}
                            <Grid item xs={12} md={4} sx={{ width: "30%" }}>
                                <TextField
                                    label="Description *"
                                    name="item_description"
                                    value={formData.item_description}
                                    onChange={handleChange}
                                    fullWidth
                                    error={formErrors.item_description}
                                    helperText={formErrors.item_description ? "Description is required" : "Detailed description of the item"}
                                    size="medium"
                                    variant="outlined"
                                    multiline
                                    minRows={2}
                                    maxRows={2}
                                    InputProps={{
                                        sx: {
                                            height: 'auto',
                                            minHeight: '56px',
                                            alignItems: 'flex-start',
                                            py: '8px'
                                        }
                                    }}
                                    FormHelperTextProps={{ sx: { mx: 0 } }}
                                />
                            </Grid>

                            {/* Action Buttons */}
                            <Grid item xs={12} sx={{ position: 'relative', top: "-30px" }}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    pt: 1,
                                    justifyContent: 'flex-end'
                                }}>
                                    <Button
                                        onClick={resetForm}
                                        variant="outlined"
                                        disabled={loading}
                                        sx={{
                                            height: '48px',
                                            width: '160px',
                                            textTransform: 'none'
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={24} /> : undefined}
                                        sx={{
                                            height: '48px',
                                            width: '160px',
                                            textTransform: 'none'
                                        }}
                                    >
                                        {editingId ? 'Update Item' : 'Create Item'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </StyledCard>

            {/* Error Display */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    icon={<ErrorIcon fontSize="inherit" />}
                >
                    <Typography variant="subtitle2" fontWeight={600}>{error}</Typography>
                </Alert>
            )}

            {/* Items Table */}
            <StyledCard>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                            <InventoryIcon />
                        </Avatar>
                    }
                    title={
                        <Typography variant="h6" component="div">
                            Items Catalog
                        </Typography>
                    }
                    subheader={`${filteredItems.length} items`}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TextField
                                select
                                size="small"
                                value={categoryFilter}
                                onChange={handleFilterChange}
                                sx={{ minWidth: 150 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FilterListIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            >
                                <MenuItem value="all">All Categories</MenuItem>
                                {CATEGORY_OPTIONS.map(option => (
                                    <MenuItem 
                                        key={option.value} 
                                        value={option.value}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        {option.icon}
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip title="More options">
                                <IconButton>
                                    <MoreIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                    sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.secondary.light, 0.05)
                    }}
                />
                <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                        {fetching && !items.length ? (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '300px',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <CircularProgress size={60} thickness={4} />
                                <Typography variant="body1" color="text.secondary">
                                    Loading Items data...
                                </Typography>
                            </Box>
                        ) : (
                            <Table size="medium">
                                <TableHead>
                                    <TableRow sx={{
                                        bgcolor: alpha(theme.palette.primary.light, 0.1),
                                        '& th': {
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            py: 2
                                        }
                                    }}>
                                            <TableCell sx={{ width: '25%' }}>Item Type</TableCell>
                                            <TableCell sx={{ width: '25%' }}>Category</TableCell>
                                        <TableCell sx={{ width: '35%' }}>Description</TableCell>
                                        <TableCell align="right" sx={{ width: '15%' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredItems.length === 0 ? (
                                        <StyledTableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}>
                                                    <InventoryIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                                                    <Typography variant="body1" color="text.secondary">
                                                        No Service items found. Create your first item above.
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={resetForm}
                                                    >
                                                        Add Item
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </StyledTableRow>
                                    ) : (
                                        items.map(item => (
                                            <StyledTableRow
                                                key={item.id}
                                                hover
                                            >
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {item.item_name}
                                                </TableCell>
                                                
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {CATEGORY_OPTIONS.find(cat => cat.value === item.category?.toLowerCase())?.icon}
                                                        <Typography variant="body2">
                                                            {CATEGORY_OPTIONS.find(cat => cat.value === item.category?.toLowerCase())?.label || item.category}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {item.item_description}
                                                    </Typography>
                                                </TableCell>
                                               
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Tooltip title="Edit item">
                                                            <IconButton
                                                                onClick={() => handleEdit(item)}
                                                                color="primary"
                                                                size="medium"
                                                                sx={{
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete item">
                                                            <IconButton
                                                                onClick={() => handleDelete(item.id)}
                                                                color="error"
                                                                size="medium"
                                                                sx={{
                                                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </StyledTableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </TableContainer>
                </CardContent>
            </StyledCard>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ bottom: { xs: 90, sm: 24 } }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%', boxShadow: theme.shadows[6] }}
                    iconMapping={{
                        success: <SuccessIcon fontSize="inherit" />,
                        error: <ErrorIcon fontSize="inherit" />,
                        info: <InfoIcon fontSize="inherit" />,
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={600}>
                        {notification.message}
                    </Typography>
                </Alert>
            </Snackbar>
        </Container>
    );
}